import React, { useState, useEffect, useRef } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../firebase';
import type { AboutContent, AboutContentSection } from '../../types';
import { useToast } from '../../hooks/useToast';
import RichTextEditor from '../../components/RichTextEditor';
import { GoogleGenAI } from '@google/genai';

const UploadIcon: React.FC<{className?: string}> = ({className}) => <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>;
const SparklesIcon: React.FC<{className?:string}> = ({className}) => <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m12 3-1.9 5.8-5.8 1.9 5.8 1.9 1.9 5.8 1.9-5.8 5.8-1.9-5.8-1.9z"/></svg>;


const AdminAboutPage: React.FC = () => {
    const [content, setContent] = useState<AboutContent | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [generatingIndex, setGeneratingIndex] = useState<number | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { showToast } = useToast();

    useEffect(() => {
        const fetchContent = async () => {
            const docRef = doc(db, 'aboutPage', 'main');
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                setContent(docSnap.data() as AboutContent);
            } else {
                setContent({ id: 'main', title: 'About Us', imageUrl: '', sections: [{ title: 'Our Philosophy', content: '' }] });
            }
            setIsLoading(false);
        };
        fetchContent();
    }, []);

    const handleSave = async () => {
        if (!content) return;
        setIsSaving(true);
        try {
            await setDoc(doc(db, 'aboutPage', 'main'), content);
            showToast('About page content saved successfully!', { type: 'success' });
        } catch (error) {
            showToast('Failed to save content.', { type: 'error' });
        } finally {
            setIsSaving(false);
        }
    };
    
    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file && content) {
            setIsUploading(true);
            try {
                const storageRef = ref(storage, `aboutPage/${file.name}_${Date.now()}`);
                await uploadBytes(storageRef, file);
                const downloadURL = await getDownloadURL(storageRef);
                setContent({ ...content, imageUrl: downloadURL });
                showToast('Image uploaded successfully.', { type: 'success' });
            } catch (error) {
                showToast('Image upload failed.', { type: 'error' });
            } finally {
                setIsUploading(false);
            }
        }
    };

    const handleSectionChange = (index: number, field: 'title' | 'content', value: string) => {
        if (content) {
            const newSections = [...content.sections];
            newSections[index] = { ...newSections[index], [field]: value };
            setContent({ ...content, sections: newSections });
        }
    };
    
    const addSection = () => {
        if (content) {
            const newSections = [...content.sections, { title: 'New Section', content: '' }];
            setContent({ ...content, sections: newSections });
        }
    };
    
    const removeSection = (index: number) => {
        if (content) {
            const newSections = content.sections.filter((_, i) => i !== index);
            setContent({ ...content, sections: newSections });
        }
    };

    const handleGenerateContent = async (index: number) => {
        if (!content) return;
        const section = content.sections[index];
        const promptText = section.content.replace(/<[^>]*>?/gm, '').trim() || section.title;

        if (!promptText) {
            showToast("Please provide a title or some initial content to improve.", { type: 'error' });
            return;
        }

        setGeneratingIndex(index);
        try {
            const ai = new GoogleGenAI({ apiKey: process.env.API_KEY as string });
            const prompt = `As a professional copywriter for an architectural firm, expand upon or rewrite the following idea into a polished paragraph suitable for an 'About Us' page. The idea is: "${promptText}". The response should be a single paragraph wrapped in <p> tags. The tone should be professional and inspiring.`;
            
            const response = await ai.models.generateContent({
                // FIX: Update deprecated model to recommended model for text generation.
                model: 'gemini-3-flash-preview',
                contents: prompt,
            });
            const text = response.text.trim();

            if (text) {
                handleSectionChange(index, 'content', text);
                showToast('Content improved successfully!', { type: 'success' });
            } else {
                throw new Error("AI did not return any content.");
            }
        } catch (error) {
            showToast("Failed to generate content.", { type: 'error' });
        } finally {
            setGeneratingIndex(null);
        }
    };

    if (isLoading) return <div>Loading...</div>;

    return (
        <div>
            <h2 className="text-3xl font-bold text-gray-800 mb-6">Edit "About Us" Page</h2>
            {content && (
                <div className="space-y-8">
                    <div className="bg-white p-8 rounded-lg shadow-md">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">Header Content</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label htmlFor="title" className="block text-sm font-medium text-gray-700">Page Title</label>
                                <input type="text" id="title" value={content.title} onChange={(e) => setContent({ ...content, title: e.target.value })} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"/>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Header Image</label>
                                <div className="relative w-full h-40 border-2 border-dashed rounded-md flex items-center justify-center cursor-pointer" onClick={() => fileInputRef.current?.click()}>
                                    {isUploading && <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10"><div className="w-8 h-8 border-2 border-dashed rounded-full animate-spin border-gray-800"></div></div>}
                                    {content.imageUrl ? <img src={content.imageUrl} alt="Header" className="h-full w-full object-cover rounded-md"/> : <div className="text-center text-gray-500"><UploadIcon className="mx-auto h-8 w-8"/><span className="mt-2 text-sm">Upload Image</span></div>}
                                    <input type="file" ref={fileInputRef} onChange={handleFileChange} className="hidden" accept="image/*"/>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white p-8 rounded-lg shadow-md">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">Content Sections</h3>
                        <div className="space-y-6">
                            {content.sections.map((section, index) => (
                                <div key={index} className="border p-4 rounded-md relative">
                                    <button onClick={() => removeSection(index)} className="absolute top-2 right-2 text-gray-400 hover:text-red-600">&times;</button>
                                    <div className="mb-4">
                                        <label className="block text-sm font-medium text-gray-700">Section Title</label>
                                        <input type="text" value={section.title} onChange={(e) => handleSectionChange(index, 'title', e.target.value)} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"/>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Content</label>
                                        <div className="relative">
                                            <RichTextEditor value={section.content} onChange={(value) => handleSectionChange(index, 'content', value)} />
                                            <button type="button" onClick={() => handleGenerateContent(index)} disabled={generatingIndex === index} className="absolute top-2 right-12 z-10 p-1.5 text-gray-500 hover:text-indigo-600 disabled:opacity-50" title="Improve with AI">
                                                <SparklesIcon className={`h-5 w-5 ${generatingIndex === index ? 'animate-pulse text-indigo-600' : ''}`} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <button onClick={addSection} className="mt-6 px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300">Add Section</button>
                    </div>

                    <div className="flex justify-end sticky bottom-4">
                        <button onClick={handleSave} disabled={isSaving} className="px-8 py-3 bg-gray-800 text-white rounded-md hover:bg-gray-700 disabled:bg-gray-400 shadow-lg">
                            {isSaving ? 'Saving...' : 'Save "About Us" Page'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminAboutPage;